import { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { UserPlus, Search, ChevronDown, ChevronUp, Ban } from "lucide-react";
import { toast } from "sonner";
import apiClient from "../services/api";
import { formatPhoneNumber, isCompletePhone } from "../lib/formatters";
import ApplicantCard from "../components/applications/ApplicantCard";
import AddApplicantDialog from "../components/applications/AddApplicantDialog";

const QUESTION_LABELS = [
  ["reliable_transportation", "Reliable transportation/insurance"],
  ["background_check", "Can pass Ft. Hood background check"],
  ["acknowledge_manual_labor", "Acknowledges outside/manual labor"],
  ["legally_authorized", "Legally authorized to work in U.S."],
  ["consent_contact", "Consents to Text/SMS & calls"],
];

const ApplicationsPage = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [positionFilter, setPositionFilter] = useState("all");
  const [passedOpen, setPassedOpen] = useState(false);
  const [editApp, setEditApp] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const fetchApps = useCallback(async () => {
    try {
      setLoading(true);
      const [appsRes, obRes] = await Promise.all([
        apiClient.get(`/applications`),
        apiClient.get(`/onboarding`),
      ]);
      const obByApp = {};
      (obRes.data.onboarding || []).forEach((o) => { obByApp[o.application_id] = o; });
      const merged = (appsRes.data.applications || []).map((a) => ({ ...a, _ob: obByApp[a.id] || null }));
      setApps(merged);
    } catch (e) {
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const onChanged = useCallback((id, patch) => {
    setApps((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }, []);

  const onRemoved = useCallback((id) => {
    setApps((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const openEdit = (a) => {
    setEditApp(a);
    setEditForm({
      first_name: a.first_name || "", last_name: a.last_name || "",
      phone: a.phone || "", email: a.email || "",
      reliable_transportation: !!a.reliable_transportation,
      background_check: !!a.background_check,
      acknowledge_manual_labor: !!a.acknowledge_manual_labor,
      legally_authorized: !!a.legally_authorized,
      consent_contact: !!a.consent_contact,
      position: a.position || "",
      years_experience: a.years_experience ?? "",
    });
  };

  const saveEdit = async () => {
    if (editForm.phone && !isCompletePhone(editForm.phone)) {
      toast.error("Please enter a complete 10-digit phone number.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editForm };
      if (!payload.position) delete payload.position;
      payload.years_experience = payload.position === "mason" && payload.years_experience !== "" ? Number(payload.years_experience) : undefined;
      const { data } = await apiClient.put(`/applications/${editApp.id}`, payload);
      onChanged(editApp.id, { ...data.application });
      toast.success("Application updated");
      setEditApp(null);
      setEditForm(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update application");
    } finally {
      setSaving(false);
    }
  };

  const { active, passed } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (a) => {
      if (!q) return true;
      const hay = `${a.first_name || ""} ${a.last_name || ""} ${a.phone || ""} ${a.email || ""}`.toLowerCase();
      return hay.includes(q);
    };
    const filtered = apps.filter((a) => matches(a) && (positionFilter === "all" || a.position === positionFilter));
    return {
      active: filtered.filter((a) => !a.passed),
      passed: filtered.filter((a) => a.passed),
    };
  }, [apps, search, positionFilter]);

  const positionCounts = useMemo(() => {
    const live = apps.filter((a) => !a.passed);
    return {
      all: live.length,
      mason: live.filter((a) => a.position === "mason").length,
      laborer: live.filter((a) => a.position === "laborer").length,
    };
  }, [apps]);

  return (
    <div className="page-enter space-y-6 max-w-5xl" data-testid="applications-page">
      <div className="page-header">
        <h1 className="text-4xl font-bold tracking-tight text-foreground">Employment Applications</h1>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, or email…"
            className="pl-9"
            data-testid="applications-search"
          />
        </div>
        <Button onClick={() => setAddOpen(true)} className="shrink-0" data-testid="add-applicant-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Add Applicant
        </Button>
        <AddApplicantDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          onAdded={(a) => setApps((prev) => [{ ...a, _ob: null }, ...prev])}
        />
        <div className="flex items-end border-b-2 border-border" role="tablist" data-testid="applications-position-filter">
          {[["all", "All"], ["mason", "Mason"], ["laborer", "Laborer"]].map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={positionFilter === value}
              onClick={() => setPositionFilter(value)}
              data-testid={`applications-filter-${value}`}
              className={`-mb-[2px] px-4 py-2 rounded-t-md text-sm font-semibold border-b-[3px] transition-colors ${
                positionFilter === value ? "border-primary text-foreground bg-primary/5" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/60"
              }`}
            >
              {label} <span className="text-xs text-muted-foreground">({positionCounts[value]})</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground py-10 text-center">Loading...</p>
      ) : apps.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <UserPlus className="w-10 h-10 mb-2 opacity-40" />
          <p>No applications yet.</p>
        </div>
      ) : (
        <>
          {active.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              {search ? "No matching applicants." : positionFilter !== "all" ? `No ${positionFilter} applicants.` : "No active applicants."}
            </p>
          ) : (
            <div className="space-y-4">
              {active.map((a) => (
                <ApplicantCard key={a.id} app={a} onChanged={onChanged} onRemoved={onRemoved} onEdit={openEdit} />
              ))}
            </div>
          )}

          {passed.length > 0 && (
            <div className="mt-8">
              <button
                type="button"
                onClick={() => setPassedOpen((v) => !v)}
                className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 mb-3"
                data-testid="passed-section-toggle"
              >
                {passedOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                <Ban className="w-4 h-4 text-slate-400" />
                Passed ({passed.length})
              </button>
              {passedOpen && (
                <div className="space-y-4">
                  {passed.map((a) => (
                    <ApplicantCard key={a.id} app={a} onChanged={onChanged} onRemoved={onRemoved} onEdit={openEdit} />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <Dialog open={!!editApp} onOpenChange={(o) => { if (!o) { setEditApp(null); setEditForm(null); } }}>
        <DialogContent data-testid="application-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Application{editApp ? ` — ${editApp.first_name} ${editApp.last_name}` : ""}</DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">First Name</Label>
                  <Input value={editForm.first_name} onChange={(e) => setEditForm((p) => ({ ...p, first_name: e.target.value }))} data-testid="edit-first-name" />
                </div>
                <div>
                  <Label className="text-sm">Last Name</Label>
                  <Input value={editForm.last_name} onChange={(e) => setEditForm((p) => ({ ...p, last_name: e.target.value }))} data-testid="edit-last-name" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Phone</Label>
                  <Input value={editForm.phone} onChange={(e) => setEditForm((p) => ({ ...p, phone: formatPhoneNumber(e.target.value) }))} data-testid="edit-phone" className={editForm.phone && !isCompletePhone(editForm.phone) ? "border-red-500 focus-visible:ring-red-500" : ""} />
                  {editForm.phone && !isCompletePhone(editForm.phone) && (
                    <p className="text-xs text-red-600 mt-1" data-testid="edit-phone-error">Enter a complete 10-digit number.</p>
                  )}
                </div>
                <div>
                  <Label className="text-sm">Email</Label>
                  <Input value={editForm.email} onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))} data-testid="edit-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Position</Label>
                  <Select value={editForm.position || "unset"} onValueChange={(v) => setEditForm((p) => ({ ...p, position: v === "unset" ? "" : v }))}>
                    <SelectTrigger data-testid="edit-position"><SelectValue placeholder="Not specified" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unset">Not specified</SelectItem>
                      <SelectItem value="mason">Mason</SelectItem>
                      <SelectItem value="laborer">Laborer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editForm.position === "mason" && (
                  <div>
                    <Label className="text-sm">Years of Experience</Label>
                    <Input type="number" min="0" value={editForm.years_experience} onChange={(e) => setEditForm((p) => ({ ...p, years_experience: e.target.value }))} data-testid="edit-years-experience" />
                  </div>
                )}
              </div>
              <div className="space-y-2 border-t pt-3">
                {QUESTION_LABELS.map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between">
                    <Label htmlFor={`edit-${key}`} className="text-sm font-normal">{label}</Label>
                    <Checkbox
                      id={`edit-${key}`}
                      checked={editForm[key]}
                      onCheckedChange={(v) => setEditForm((p) => ({ ...p, [key]: !!v }))}
                      data-testid={`edit-${key}`}
                    />
                  </div>
                ))}
                <p className="text-xs text-slate-400">Checked = Yes</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditApp(null); setEditForm(null); }} data-testid="edit-cancel">Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="edit-save">{saving ? "Saving..." : "Save Changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApplicationsPage;
